import axios, { AxiosResponse } from "axios";
import { Feature } from "ol";
import { EsriJSON } from "ol/format";
import { Geometry } from "ol/geom";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import { addLayerData } from "./clusterFunctions";
import { config, defaultOption, proxyUrl } from "./config";
import { AttachmentType, PrintType } from "./enums";
import { IdentifyResult, IdentifyResultsArray } from "./Identify/identifyUtils";
import { IEsriFieldInfo, IFeatureLayerData } from "./interfaces";
import { IToken } from "./MapDownload/Token";
import { getMultiPolygonGeometry } from "./utils";

interface IObjectIDs {
    objectId: number;
    globalId: string;
}

interface IAttachmentPath {
    type: AttachmentType;
    url: string;
}
interface IAttachmentFile {
    type: AttachmentType;
    endpoint: string;
    resourceId: string;
}

const esrijsonFormat = new EsriJSON();

const fieldInfoCache: Record<number, IEsriFieldInfo[]> = {};

export const fetchFieldInfo = (olLayer: VectorLayer<VectorSource>, url: string): void => {
    const params = {
        f: "json",
        returnGeometry: true,
        where: "1=1",
        outFields: "*",
        outSR: "3059"
    };

    axios
        .get(url, { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            olLayer.set("fieldInfo", response.data.fields);
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};
export const getFieldInfo = (): IEsriFieldInfo[] => fieldInfoCache[0];

const fixData = (features: Feature[], fieldInfos: IEsriFieldInfo[]) => {
    const decimalFields = getDecimalFields(fieldInfos);

    for (const feature of features) {
        for (const fieldName of decimalFields) {
            const value = feature.get(fieldName);
            if (value !== null) {
                feature.set(fieldName, parseFloat((value as number).toFixed(8)), true);
            }
        }
    }
};

const getDecimalFields = (fieldInfos: IEsriFieldInfo[]): string[] => {
    return fieldInfos
        .filter((f) => f.type === "esriFieldTypeDouble" || f.type === "esriFieldTypeSingle")
        .map((f) => f.name);
};

export const executeQuery = async (layerUrl, query): Promise<Feature[]> => {
    return axios
        .get(layerUrl + "/query", { params: query })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const features = esrijsonFormat.readFeatures(response.data);
            features.length && fixData(features, response.data.fields);

            return features;
        })
        .catch((err) => {
            console.error(err);
            return [];
        });
};

export const loadSourceFeatures = (
    url: string,
    ext: number[],
    source: VectorSource,
    layerData?: IFeatureLayerData
): void => {
    const params = {
        f: "json",
        returnGeometry: true,
        outFields: "*",
        outSR: "3059",
        where: "1=1",
        geometry: `{xmin: ${ext[0]}, ymin:${ext[1]}, xmax:${ext[2]}, ymax:${ext[3]}, spatialReference:{wkid:3059}}`
    };

    axios
        .get(url + "/query", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const features = esrijsonFormat.readFeatures(response.data);

            if (features.length) {
                layerData && addLayerData(features, layerData);
                source.addFeatures(features);
            }
        })
        .catch((err) => console.error(err));
};

export const getAllFeatures = (url: string, returnZ?: boolean): Promise<Feature<Geometry>[]> => {
    const params = {
        f: "json",
        returnGeometry: true,
        outFields: "*",
        outSR: "3059",
        where: "1=1"
    };
    returnZ && (params["returnZ"] = true);

    return axios
        .get(url + "/query", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            return esrijsonFormat.readFeatures(response.data);
        })
        .catch((err) => {
            console.error(err);
            const empty: Feature<Geometry>[] = [];
            return empty;
        });
};

export const getSubLayers = async (url: string) => {
    const params = { f: "json" };

    //     .get("https://astmap.lvm.lv/beta/proxy/LV53B2653DD8BA462AAD45443E6E906BBB/MapServer/layers", { params })

    return axios
        .get(url, { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            return response.data;
        })
        .catch((err) => {
            return [];
        });
};

export const identify = async (url: string, params, mapLayerId: string): Promise<IdentifyResultsArray> => {
    return axios
        .get(url + "/identify", { params })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            const results: IdentifyResultsArray = [];
            response.data.results?.map((res) => {
                let layerElement = results.find((resEl) => resEl[0] === res.layerName);
                if (!layerElement) {
                    layerElement = [res.layerName, []];
                    results.push(layerElement);
                }

                layerElement[1].push({
                    mapLayerId: mapLayerId,
                    layerId: res.layerId,
                    layerName: res.layerName,
                    feature: esrijsonFormat.readFeatures({ features: [res] })[0]
                } as IdentifyResult);
            });

            return results;
        })
        .catch((err) => {
            console.error(err);
            const results: IdentifyResultsArray = [];
            return results;
        });
};

export const getToken = (url: string, username: string, password: string): Promise<{ success: boolean; data: any }> =>
    axios
        .get(url, {
            params: {
                user: username,
                password: password
            }
        })
        .then(
            (response) =>
                response.data.tocken &&
                response.data.expires && {
                    success: true,
                    data: {
                        token: response.data.tocken,
                        expires: response.data.expires,
                        extraLayers: response.data.extraLayers
                    }
                }
        )
        .catch((error) => ({
            success: false,
            data: error?.response?.data?.error && error.response.data
        }));

export interface ISheetDownloadResult {
    success: boolean;
    fileUrl: string;
    error: any;
}

export const downloadMapSheets = (
    gridNumbers: string[],
    authToken: string,
    mapDownloadUrl: string
): Promise<ISheetDownloadResult> =>
    axios
        .post(
            mapDownloadUrl,
            {
                tocken: authToken,
                grid_items: gridNumbers
            },
            {
                headers: { "Content-Type": "application/x-www-form-urlencoded; charset=UTF-8" }
            }
        )
        .then((response) => {
            if (response.data.file) {
                return { success: true, fileUrl: response.data.file, error: null };
            }
            return { success: false, fileUrl: "", error: null };
        })
        .catch((error) => {
            return { success: false, fileUrl: "", error: error?.response?.data?.error && error.response.data };
        });

export const executeQueryAndReturnFieldInfo = async (layerUrl, query) => {
    return axios
        .get(layerUrl + "/query", { params: query })
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }
            const features = esrijsonFormat.readFeatures(response.data);
            features.length && fixData(features, response.data.fields);

            return { fieldInfo: response.data.fields, features };
        })
        .catch((err) => {
            console.error(err);
            return null;
        });
};

export const getWmsCapabilities = async (layerUrl) => {
    return axios
        .get(layerUrl + "?SERVICE=WMS&REQUEST=GetCapabilities")
        .then((response) => {
            if (response.data.error) {
                throw new Error(response.data.error.message + "\n" + response.data.error.details.join("\n"));
            }

            return response.data;
        })
        .catch((err) => {
            console.error(err);
            return null;
        });
};
